import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, RefreshCw, Settings, ClipboardList } from 'lucide-react';
import { checkCallAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { CheckCallDialog, CheckCallHistoryDialog, CONDITION_COLORS } from './CheckCallDialog';
import { MissedReportDialog } from './MissedReportDialog';
import { CarrierStatusBadge } from '../StatusBadge';
import { toast } from 'sonner';

const COLUMNS = [
  { id: 'pickups', title: 'Pickups', sub: 'Today · confirm 2h before' },
  { id: 'deliveries', title: 'Deliveries', sub: 'Today · confirm 2h before' },
  { id: 'transit', title: 'In-Transit Updates', sub: 'Today · daily update' },
  { id: 'evening', title: 'Evening Prep Calls', sub: "Today · for tomorrow's deliveries" },
  { id: 'tomorrow_pickups', title: "Tomorrow's Pickups", sub: 'Tomorrow' },
  { id: 'tomorrow_deliveries', title: "Tomorrow's Deliveries", sub: 'Tomorrow' },
];

// Urgency from the task's timezone-aware due instant vs now
export const priorityFor = (item, nowMs = Date.now()) => {
  if (item.completed) return 'checked';
  const due = new Date(item.due_utc).getTime();
  const diffH = (due - nowMs) / 3600000;
  if (diffH < 0) return 'overdue';
  if (diffH <= 1) return 'urgent';
  if (diffH <= 2) return 'due';
  return 'normal';
};

const PRIORITY_STYLE = {
  normal: { label: null, chip: '', border: 'border-[var(--tms-border)]' },
  due: { label: 'Check Call Due', chip: 'bg-[var(--tms-warning)]/15 text-[var(--tms-warning)] border border-[var(--tms-warning)]/40', border: 'border-[var(--tms-warning)]/60' },
  urgent: { label: 'Urgent', chip: 'bg-[var(--tms-error)]/15 text-[var(--tms-error)] border border-[var(--tms-error)]/40', border: 'border-[var(--tms-error)]/60' },
  overdue: { label: 'Overdue', chip: 'bg-[var(--tms-error)] text-white', border: 'border-[var(--tms-error)]' },
  checked: { label: '✓ Checked', chip: 'bg-[var(--tms-success)]/15 text-[var(--tms-success)] border border-[var(--tms-success)]/40', border: 'border-[var(--tms-success)]/50' },
};

const CheckCallCard = ({ item, now, onCheckCall, onOpen, onHistory }) => {
  const p = priorityFor(item, now);
  const style = PRIORITY_STYLE[p];
  const lc = item.completed_by;
  const isTransit = item.task_type === 'transit_daily';
  const cond = item.condition ? CONDITION_COLORS[item.condition] : null;
  const typeLabel = isTransit ? 'IN TRANSIT' : item.task_type === 'evening_before' ? 'EVENING PREP · DELIVERY' : item.stop_type === 'pickup' ? 'PICKUP' : 'DELIVERY';
  return (
    <div
      className={`kanban-card relative border-2 ${style.border}`}
      style={cond ? { background: cond.background } : { background: '#fff' }}
      data-testid={`check-call-card-${item.key}`}
      data-priority={p}
      data-condition={item.condition || ''}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <button className="font-mono text-sm font-bold text-[var(--tms-text)] hover:underline" onClick={() => onOpen(item.load_id)} data-testid={`check-call-card-load-${item.key}`}>
          {item.load_number || item.load_id.slice(0, 8).toUpperCase()}
        </button>
        <div className="flex items-center gap-1">
          {cond && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ background: cond.border, color: '#fff' }} data-testid={`check-call-condition-${item.key}`}>{item.condition}</span>}
          {style.label && <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${style.chip}`} data-testid={`check-call-priority-${item.key}`}>{style.label}</span>}
        </div>
      </div>

      <div className="mb-2">
        <div className="text-xs text-[var(--tms-text-muted)] font-medium">{typeLabel} · <span className="text-[var(--tms-text-secondary)]">{item.task_label}</span></div>
        {item.appointment_local_display && (
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-lg font-bold text-[var(--tms-text)]" data-testid={`check-call-local-time-${item.key}`}>{item.appointment_local_display}</span>
            {!item.same_timezone && <span className="text-xs text-[var(--tms-text-muted)]" data-testid={`check-call-central-time-${item.key}`}>{item.appointment_central_display}</span>}
            {isTransit && <span className="text-xs text-[var(--tms-text-muted)]">next delivery</span>}
          </div>
        )}
        <div className="text-xs font-semibold text-[var(--tms-text-secondary)]" data-testid={`check-call-due-${item.key}`}>Due {item.due_central_display}{item.due_date_central !== item.appointment_date_central && item.due_date_central ? ` · ${item.due_date_central}` : ''}</div>
        {item.facility_name && <div className="text-sm font-medium text-[var(--tms-text)] truncate" title={item.facility_name}>{item.facility_name}</div>}
        {(item.city || item.state) && <div className="text-xs text-[var(--tms-text-muted)]">{[item.city, item.state].filter(Boolean).join(', ')}</div>}
      </div>

      <div className="text-xs text-[var(--tms-text-secondary)] space-y-0.5 mb-2 pb-2 border-b border-[var(--tms-border)]">
        <div className="truncate"><span className="text-[var(--tms-text-muted)]">Customer</span> {item.customer_name || '—'}</div>
        <div className="truncate"><span className="text-[var(--tms-text-muted)]">Carrier</span> {item.carrier_name || '—'}</div>
        <div className="truncate flex items-center gap-1">
          <span className="text-[var(--tms-text-muted)]">Driver</span> {item.driver_name || '—'}
          {item.driver_phone && <a href={`tel:${item.driver_phone}`} className="inline-flex items-center gap-1 text-[var(--tms-primary)] ml-1"><Phone className="w-3 h-3" />{item.driver_phone}</a>}
        </div>
        <div className="flex items-center gap-2 pt-1">
          <span className="text-[var(--tms-text-muted)]">Load</span><span className="font-semibold">{item.load_status}</span>
          {item.carrier_status && <CarrierStatusBadge status={item.carrier_status} />}
        </div>
      </div>

      {lc && (
        <button className="text-left text-xs mb-2 w-full hover:underline" onClick={() => onHistory(item)} title="View check call history" data-testid={`check-call-checked-${item.key}`}>
          <span className="font-bold text-[var(--tms-success)]">✓ Checked</span>
          <span className="text-[var(--tms-text-secondary)]"> {lc.checked_at_central_display} • {lc.checked_by_name}</span>
          {(lc.driver_status || lc.eta || lc.location) && (
            <div className="text-[var(--tms-text-muted)]">{[lc.driver_status, lc.location, lc.eta ? `ETA ${lc.eta}` : null].filter(Boolean).join(' • ')}</div>
          )}
          {(lc.satisfies_task_keys || []).length > 1 && <div className="text-[var(--tms-text-muted)] italic">also satisfied today's transit update</div>}
        </button>
      )}

      <button
        onClick={() => onCheckCall(item)}
        className={`w-full h-8 rounded text-xs font-semibold transition-colors ${lc ? 'border border-[var(--tms-border)] text-[var(--tms-text-secondary)] hover:bg-[var(--tms-hover)]' : 'bg-[var(--tms-primary)] text-white hover:opacity-90'}`}
        data-testid={`check-call-btn-${item.key}`}
      >
        {lc ? 'New Check Call' : 'Check Call'}
      </button>
    </div>
  );
};

const DeadlineSettings = ({ settings, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(settings);
  useEffect(() => setForm(settings), [settings]);
  const save = async () => {
    try { const r = await checkCallAPI.saveSettings(form); onSaved(r.data); setOpen(false); toast.success('Deadlines updated'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };
  return (
    <div className="relative">
      <button className="inline-flex items-center gap-1 hover:text-[var(--tms-text)]" onClick={() => setOpen(o => !o)} data-testid="check-call-settings-btn"><Settings className="w-3 h-3" /> Deadlines</button>
      {open && (
        <div className="absolute right-0 top-6 z-30 bg-[var(--tms-card)] border border-[var(--tms-border)] rounded-lg shadow-lg p-3 w-64 text-xs space-y-2" data-testid="check-call-settings-popover">
          <label className="flex items-center justify-between gap-2">Transit update by (CT)<input type="time" className="border rounded px-1 h-7" value={form.transit_deadline} onChange={e => setForm({ ...form, transit_deadline: e.target.value })} data-testid="check-call-setting-transit" /></label>
          <label className="flex items-center justify-between gap-2">Evening prep by (CT)<input type="time" className="border rounded px-1 h-7" value={form.evening_deadline} onChange={e => setForm({ ...form, evening_deadline: e.target.value })} data-testid="check-call-setting-evening" /></label>
          <div className="flex justify-end gap-2 pt-1">
            <button className="px-2 h-7 rounded border" onClick={() => setOpen(false)}>Cancel</button>
            <button className="px-2 h-7 rounded bg-[var(--tms-primary)] text-white" onClick={save} data-testid="check-call-settings-save">Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const CheckCallsBoard = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [dialogItem, setDialogItem] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const fetchQueue = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try { setData((await checkCallAPI.queue()).data); }
    catch { toast.error('Failed to load check calls'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 30000);
    const refresh = setInterval(() => fetchQueue(true), 120000);
    return () => { clearInterval(tick); clearInterval(refresh); };
  }, [fetchQueue]);

  // Apply a saved check call to every task it satisfies (and refresh stop condition) without reload
  const handleSaved = (rec) => {
    const keys = new Set((rec.satisfies_task_keys || []).map(k => `${rec.load_id}:${k}`));
    setData(prev => {
      if (!prev) return prev;
      const cols = {};
      for (const [k, items] of Object.entries(prev.columns)) {
        cols[k] = items.map(i => {
          if (keys.has(i.key)) return { ...i, completed: true, completed_by: rec, condition: rec.condition || i.condition };
          if (rec.stop_index !== null && rec.stop_index !== undefined && i.load_id === rec.load_id && i.stop_index === rec.stop_index && !i.completed && rec.condition) return { ...i, condition: rec.condition };
          return i;
        });
      }
      return { ...prev, columns: cols };
    });
  };

  if (loading && !data) return <div className="text-sm text-[var(--tms-text-secondary)] py-8">Loading check calls…</div>;

  const counts = (items) => {
    const c = { overdue: 0, unchecked: 0 };
    items.forEach(i => { const p = priorityFor(i, now); if (p === 'overdue') c.overdue++; if (p !== 'checked') c.unchecked++; });
    return c;
  };

  return (
    <div data-testid="check-calls-board">
      <div className="flex items-center justify-between mb-3 text-xs text-[var(--tms-text-muted)]">
        <span>Operational time zone: <b>Central (America/Chicago)</b> · Today {data?.today} · Tomorrow {data?.tomorrow} · Transit update by {data?.settings?.transit_deadline} · Evening prep by {data?.settings?.evening_deadline}</span>
        <div className="flex items-center gap-4">
          <button className="inline-flex items-center gap-1 hover:text-[var(--tms-text)]" onClick={() => setReportOpen(true)} data-testid="missed-report-btn"><ClipboardList className="w-3 h-3" /> Missed Report</button>
          {isAdmin() && data?.settings && <DeadlineSettings settings={data.settings} onSaved={() => fetchQueue(true)} />}
          <button className="inline-flex items-center gap-1 hover:text-[var(--tms-text)]" onClick={() => fetchQueue()} data-testid="check-calls-refresh"><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map(col => {
          const items = data?.columns?.[col.id] || [];
          const c = counts(items);
          return (
            <div key={col.id} className="kanban-column" data-testid={`check-call-column-${col.id}`}>
              <div className="p-3 border-b border-[var(--tms-border)] flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-[var(--tms-text)]">{col.title}</h3>
                  <span className="text-xs text-[var(--tms-text-muted)]">{items.length} task{items.length !== 1 ? 's' : ''} · {col.sub}</span>
                </div>
                <div className="flex gap-1">
                  {c.overdue > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--tms-error)] text-white" title="Overdue" data-testid={`check-call-overdue-count-${col.id}`}>{c.overdue}</span>}
                  {c.unchecked > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[var(--tms-warning)]/20 text-[var(--tms-warning)]" title="Unchecked">{c.unchecked}</span>}
                </div>
              </div>
              <div className="p-3 space-y-2 min-h-[400px]">
                {items.length === 0 && <p className="text-xs text-[var(--tms-text-muted)] text-center py-6">Nothing due</p>}
                {items.map(item => (
                  <CheckCallCard key={item.key} item={item} now={now} onCheckCall={setDialogItem} onHistory={setHistoryItem} onOpen={(id) => navigate(`/loads/${id}`)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <CheckCallDialog open={!!dialogItem} onOpenChange={(o) => { if (!o) setDialogItem(null); }} item={dialogItem} onSaved={handleSaved} />
      <MissedReportDialog open={reportOpen} onOpenChange={setReportOpen} />
      <CheckCallHistoryDialog
        open={!!historyItem}
        onOpenChange={(o) => { if (!o) setHistoryItem(null); }}
        loadId={historyItem?.load_id}
        stopIndex={historyItem?.task_type === 'transit_daily' ? undefined : historyItem?.stop_index}
        taskKey={historyItem?.task_type === 'transit_daily' ? historyItem?.task_key : undefined}
        title={historyItem ? `${historyItem.load_number || ''} · ${historyItem.task_type === 'transit_daily' ? 'In-Transit Updates' : historyItem.facility_name || ''}` : ''}
      />
    </div>
  );
};
